import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

/** A registry user as the admin page sees them (FOC-5953). */
export interface AppUser {
  id: string;
  displayName: string;
  mail: string;
  /** Whether the Assigned To picker on the document page offers them. */
  isAssignable: boolean;
  /** Null for somebody an admin added who has not signed in yet. */
  firstSeenAt: string | null;
  lastSeenAt: string | null;
}

export interface DirectoryUserOption {
  key: string;
  label: string;
  value: string;
  mail: string;
}

export const fetchAppUsers = createAsyncThunk("appUsers/fetch", async () => {
  return axios.get("app-users").then((res: any) => res.data?.data ?? []);
});

/** Turns one user's Assigned To availability on or off. Resolves to the updated user. */
export const setAppUserAssignable = async (id: string, isAssignable: boolean): Promise<AppUser> => {
  return axios.patch(`app-users/${id}`, { isAssignable }).then((res: any) => res.data?.data);
};

/**
 * Tenant directory search for Add user. Unlike the Assigned To picker's own search this is not
 * narrowed to people already in the registry — finding those who are not is the point.
 */
export const searchDirectoryUsers = async (search: string): Promise<DirectoryUserOption[]> => {
  if (search.length < 3) {
    return [];
  }
  return axios
    .get(`app-users/directory-search?search=${encodeURIComponent(search)}`)
    .then((res: any) => {
      if (res?.status !== 200 || !res.data?.succeeded) {
        return [];
      }
      return (res.data.data ?? []).map((d: any) => ({
        key: d.id,
        value: d.id,
        label: d.displayName ?? d.mail,
        mail: d.mail
      }));
    })
    .catch(() => []);
};

/** Adds a directory user to the registry as assignable. Resolves to the stored user. */
export const addAppUser = async (user: { id: string; displayName: string; mail?: string }): Promise<AppUser> => {
  return axios.post("app-users", user).then((res: any) => res.data?.data);
};

const appUsersSlice = createSlice({
  name: "appUsers",
  initialState: {
    loading: false,
    data: [] as AppUser[],
  },
  reducers: {
    /**
     * Flips a row without waiting for the server, so the switch does not lag behind the click.
     * The caller flips it back if the request fails.
     */
    applyAssignable(state, action: PayloadAction<{ id: string; isAssignable: boolean }>) {
      const user = state.data.find((u) => u.id === action.payload.id);
      if (user) {
        user.isAssignable = action.payload.isAssignable;
      }
    },
    upsertAppUser(state, action: PayloadAction<AppUser>) {
      const existing = state.data.findIndex((u) => u.id === action.payload.id);
      if (existing >= 0) {
        state.data[existing] = action.payload;
      } else {
        state.data.push(action.payload);
      }
      state.data.sort((a, b) => (a.displayName ?? "").localeCompare(b.displayName ?? "", undefined, { sensitivity: "base" }));
    },
  },
  extraReducers: {
    [fetchAppUsers.fulfilled.type]: (state, action) => {
      state.data = action.payload;
      state.loading = false;
    },
    [fetchAppUsers.pending.type]: (state) => {
      state.loading = true;
    },
    [fetchAppUsers.rejected.type]: (state) => {
      state.loading = false;
    },
  },
});

export const { applyAssignable, upsertAppUser } = appUsersSlice.actions;
export default appUsersSlice.reducer;
